import { Button, Col, Form, Row } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RouteChildrenProps, useHistory, useLocation } from "react-router";
import { AutoRedirectAfterSubmitSuccessfully, ReportActivitiesEnum, ReportStatesEnum, ReportStatusEnum } from "../../../../../constants/constants";
import { ROUTE_PATHS } from "../../../../../constants/url-config";
import { FormSectionModel } from "../../../../../models/form-section.model";
import { ReviewModel } from "../../../../../models/review.model";
import { ScenarioModel } from "../../../../../models/scenario.model";
import { setActions, setStatus, setTitle } from "../../../../../redux/actions/header.action";
import { ECAPService } from "../../../../../services/ecap.service";
import { ERMService } from "../../../../../services/erm.service";
import { DATE_UTIL } from "../../../../../utils/dateUtils";
import i18n from "../../../../../utils/i18n";
import { ROLE_GUARD } from "../../../../../utils/role-guard";
import UTIL from "../../../../../utils/utils";
import AppAnchor from "../../../../atoms/anchor";
import FooterAction from "../../../../atoms/footer-action";
import AppReview from "../../../../atoms/review";
import AppSubmitedForm from "../../../../atoms/submited-form";
import DivisionViewSection from "./division-section";
import EstimatedDirectImpactViewSection from "./estimated-direct-impact-section";
import EstimatedIndirectImpactViewSection from "./estimated-indirect-impact-section";
import EstimateLikelihoodViewSection from "./estimated-likelihood-section";
import InformationViewSection from "./information-section";
import PlaningViewSection from "./planing-section";
import RecoveryViewSection from "./recovery-information-section";
import RiskTypeViewSection from "./risk-type-section";
import "./scenarios-view.scss";

export interface ScenarioSectionProp {
    data: ScenarioModel
}


const ScenariosFormSections: FormSectionModel[] = [
    {
        title: i18n.t("Scenario.planingTitle"),
        id: 1,
        is_completed: false,
        sectionComponent: PlaningViewSection
    },
    {
        title: i18n.t("Scenario.divisionSectionTitle"),
        id: 2,
        is_completed: false,
        sectionComponent: DivisionViewSection
    },
    {
        title: i18n.t("Scenario.informationTitle"),
        id: 3,
        is_completed: false,
        sectionComponent: InformationViewSection
    },
    {
        title: i18n.t("Scenario.estimatedLikelihoodTitle"),
        id: 4,
        is_completed: false,
        sectionComponent: EstimateLikelihoodViewSection
    },
    {
        title: i18n.t("Scenario.estimatedDirectImpactTitle"),
        id: 5,
        is_completed: false,
        sectionComponent: EstimatedDirectImpactViewSection
    },
    {
        title: i18n.t("Scenario.estimatedIndirectImpactTitle"),
        id: 6,
        is_completed: false,
        sectionComponent: EstimatedIndirectImpactViewSection
    },
    {
        title: i18n.t("Scenario.expectedRecoveryTitle"),
        id: 7,
        is_completed: false,
        sectionComponent: RecoveryViewSection
    },
    {
        title: i18n.t("Scenario.riskTypeSectionTitle"),
        id: 8,
        is_completed: false,
        sectionComponent: RiskTypeViewSection
    }
]

interface Props extends RouteChildrenProps<{ id: string }> { }

const ScenarioView = (props: Props) => {

    const history = useHistory();
    const { t } = useTranslation();
    const [data, setData] = useState<ScenarioModel>({});
    const [isSubmited, setIsSubmited] = useState(false);
    const id = useRef(0);
    const dispatch = useDispatch();
    const [reviewData, setReviewData] = useState<ReviewModel[]>();
    const [newReview, setNewReview] = useState<ReviewModel>({});
    const [isApprovalWFL, setIsApprovalWFL] = useState(false);
    const location = useLocation();
    const isOwner = useRef<boolean>(false);

    const setHeaderTitle = (title: string) => dispatch(setTitle(title));

    const setHeaderActions = (actions: React.ReactNode) => dispatch(setActions(actions));


    const submit = () => {
        ERMService.saveDraftLostScenarioAsync(id.current, { submit_state: ReportStatesEnum.Submitted }, true).then(res => {
            setHeaderActions("");
            setIsSubmited(true);
            setTimeout(() => {
                props.history.push(ROUTE_PATHS.Scenarios);
            }, AutoRedirectAfterSubmitSuccessfully);
        });
    }

    const edit = () => {
        history.push(`${ROUTE_PATHS.EditScenario}/${id.current}`);
    }

    const mitigation = (submit_state: ReportStatesEnum | undefined) => {
        if (UTIL.checkCanEditByState(submit_state) && isOwner.current) {
            history.push(`${ROUTE_PATHS.ScenarioMitigation}/${id.current}`);
        } else {
            history.push(`${ROUTE_PATHS.ScenarioMitigationView}/${id.current}`);
        }

    }

    const startMitigation = () => {
        history.push(`${ROUTE_PATHS.ScenarioMitigation}/${id.current}`);
    }

    const appraisal = (submit_state: ReportStatesEnum | undefined) => {
        if (isOwner.current && UTIL.checkCanEditByState(submit_state)) {
            history.push(`${ROUTE_PATHS.ScenarioAppraisal}/${id.current}`);
        } else {
            history.push(`${ROUTE_PATHS.ScenarioAppraisalView}/${id.current}`);
        }

    }

    const renderActions = (state: string | undefined) => {

        if (!isOwner.current) {
            return null;
        }

        switch (state) {
            case ReportStatesEnum.Approved:
                if (data.scenario_mitigation && data.scenario_mitigation.id) {
                    return (<>
                        <Button onClick={() => mitigation(data.scenario_mitigation?.submit_state)} htmlType="button" type="primary" shape="round" block >{t("Scenario.lbMitigation")}</Button>
                        {data.appraisal_tool && <Button onClick={() => appraisal(data.appraisal_tool?.submit_state)} htmlType="button" type="primary" shape="round" block >{t("Scenario.lbProjectAppraisal")}</Button>}
                    </>)
                }

                return <Button onClick={startMitigation} htmlType="button" type="primary" shape="round" block >{t("Scenario.lbStartMitigation")}</Button>
            case ReportStatesEnum.Submitting:
                return <>
                    <Button onClick={edit} htmlType="button" type="default" shape="round" block >{t("Scenario.btnGoback")}</Button>
                    <Button onClick={submit} htmlType="button" type="primary" shape="round" block >{t("Scenario.lbSubmit")}</Button>
                </>
            case ReportStatesEnum.Rejected:
            case ReportStatesEnum.Draft:
                return <Button onClick={edit} htmlType="button" type="primary" shape="round" block >{t("Scenario.btnEdit")}</Button>
            case ReportStatesEnum.New:
                return <>
                    {/* <Button htmlType="button" type="default" shape="round" block >{t("Scenario.btnWithdraw")}</Button> */}
                    <Button onClick={edit} htmlType="button" type="primary" shape="round" block >{t("Scenario.btnEdit")}</Button>
                </>
            default:
                return null;
        }
    }

    const comment = (message: string) => {
        ERMService.createScenarioCommentAsync(id.current, message).then(res => {
        })
    }

    const approve = () => {
        ECAPService.approveOrReferScenarioAsync(id.current, ReportStatusEnum.Approved).then(res => {
            setData(pre => {
                pre.submit_state = ReportStatesEnum.Approved;
                updateStatus(ReportStatesEnum.Approved);
                return pre;
            })

            setNewReview({ action: ReportActivitiesEnum.Approve })
        });
    }

    const refer = () => {
        ECAPService.approveOrReferScenarioAsync(id.current, ReportStatusEnum.Referred).then(res => {
            setData(pre => {
                pre.submit_state = ReportStatesEnum.Rejected;
                updateStatus(ReportStatesEnum.Rejected);
                return pre;
            })
            setNewReview({ action: ReportActivitiesEnum.Refer })
        });
    }

    const updateStatus = (submit_state: string | undefined) => {
        const dom = UTIL.getLossStatesDOM(submit_state);
        const status = <span className="text-status" style={{ color: dom.color, backgroundColor: dom.bgColor }}>
            {dom.text}
        </span>

        dispatch(setStatus(status));
    }

    useEffect(() => {
        id.current = Number(props.match?.params.id);
        setIsApprovalWFL(location.pathname.includes(ROUTE_PATHS.ApprovalWorkflowScenario));

        ERMService.getLostScenarioAsyns(id.current).then(res => {
            const newData: ScenarioModel = res.data;
            UTIL.setSectionIsCompleted(newData._section_statuses, ScenariosFormSections);
            isOwner.current = ROLE_GUARD.isCurrentUser(newData.created_by);
            setData(newData);

            if (newData.submit_state === ReportStatesEnum.Submitting) {
                setHeaderTitle(t("Scenario.formHeaderTitle"));
            } else {
                setHeaderTitle(newData.short_name_for_scenario || " ");
                updateStatus(newData.submit_state);
            }

            setHeaderActions(renderActions(newData.submit_state));
        });

        ERMService.getScenarioCommentsAsync(id.current).then(res => {
            setReviewData(res.data);
        });

        return () => {
            setHeaderTitle("");
            setHeaderActions("");
            dispatch(setStatus(""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    return isSubmited ? <AppSubmitedForm title={t("Scenario.submitedText")} description={t("Scenario.submitedDes")} /> : (
        <div className='scenario-view-wrapper'>
            <Row gutter={20}>
                <Col xs={0} sm={5}>
                    <AppAnchor sections={ScenariosFormSections} />
                </Col>
                <Col xs={0} sm={14}>
                    <Form
                        layout="vertical"
                        className="main-form"
                    >
                        <div className="form-header">
                            {
                                data.submit_state === ReportStatesEnum.Submitting ?
                                    <div className="title">
                                        {t("Scenario.lbReviewData")}
                                    </div> :
                                    <>
                                        <div className="title">
                                            {data.short_name_for_scenario}
                                        </div>
                                        <div className="sub-title">
                                            {t("ControlFailure.lbCreatedOn")} {DATE_UTIL.convertServerToViewDateTimeFormat(data.created_on)}
                                        </div>
                                    </>
                            }
                        </div>

                        {ScenariosFormSections.map((item, index) => (
                            <div id={`section${item.id}`} key={index} className='form-section'>
                                <p className='section-title'>
                                    {item.title}
                                </p>
                                <item.sectionComponent data={data} />
                            </div>
                        ))}

                        <Row gutter={12}>
                            <Col span={24} className="form-action">
                                {
                                    (data.submit_state === ReportStatesEnum.New ||
                                        data.submit_state === ReportStatesEnum.Draft ||
                                        data.submit_state === ReportStatesEnum.Submitting) &&
                                    renderActions(data.submit_state)
                                }
                            </Col>
                        </Row>

                    </Form>
                </Col>
                <Col xs={0} sm={5}>
                    {UTIL.CheckShowReviewByState(data.submit_state) &&
                        <AppReview newReview={newReview} comment={comment} data={reviewData} showInputComment={ROLE_GUARD.isApprover(data.approval_name)} />}
                </Col>
            </Row>

            {data.submit_state === ReportStatesEnum.Submitted && ROLE_GUARD.isApprover(data.approval_name) && isApprovalWFL && <FooterAction onApprove={approve} onRefer={refer} />}
        </div>

    )
}

export default ScenarioView;
